import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  Menu,
  MenuItem,
  Box,
  Collapse,
  Divider,
  ListItemText,
  Typography,
  useMediaQuery,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GUIDE_GROUPS from 'src/constants/guideLinks';
import { useLocation } from 'react-router-dom';
import HelpModule from './HelpModule';
import DataPolicyDialog from 'src/views/utilities/DataPolicy';
import FeedbackDialog from './FeedbackDialog';

const HelpMenu = ({ inline = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openGettingStarted, setOpenGettingStarted] = useState(false);
  const [openDataPolicy, setOpenDataPolicy] = useState(false);
  const [openFeedback, setOpenFeedback] = useState(false);
  const [openGuides, setOpenGuides] = useState(false);
  // Popover computes its top/left once when it opens. Expanding Guides changes
  // the paper height, so without re-running that the menu drifts and stays
  // drifted after collapsing again.
  const menuActionRef = useRef(null);
  const repositionMenu = () => menuActionRef.current?.updatePosition();
  const isMobile = useMediaQuery('(max-width: 762px)');
  const location = useLocation();

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setOpenGuides(false);
  };

  const handleOpenGettingStarted = () => {
    setOpenGettingStarted(true);
    handleMenuClose();
  };

  const handleCloseGettingStarted = () => {
    setOpenGettingStarted(false);
  };

  const handleOpenDataPolicy = () => {
    setOpenDataPolicy(true);
    handleMenuClose();
  };

  const handleCloseDataPolicy = () => {
    setOpenDataPolicy(false);
  };

  const handleOpenFeedback = () => {
    setOpenFeedback(true);
    handleMenuClose();
  };

  const handleCloseFeedback = () => {
    setOpenFeedback(false);
  };

  // const handleCoffeeClick = () => {
  //   window.open('https://www.buymeacoffee.com/zosa', '_blank');
  // };

  // Only hide if NOT inline and either on mobile or on the getting-started page
  if (!inline && (isMobile || location.pathname === '/getting-started')) {
    return null;
  }

  // Conditionally apply positioning style
  const buttonStyle = !inline
    ? { position: 'fixed', bottom: '16px', right: '16px', zIndex: 9999 }
    : {marginLeft: 0};

  return (
    <>
      <IconButton
        edge="start"
        color="primary.main"
        aria-label="more from ZOSA"
        onClick={handleMenuClick}
        style={buttonStyle}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        keepMounted
        action={menuActionRef}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 340,
              // Caps how far the paper can grow, so expanding Guides scrolls
              // inside the menu instead of resizing it past the viewport.
              maxHeight: 'min(70vh, 480px)',
              borderRadius: 2,
            },
          },
        }}
      >
        <MenuItem onClick={handleMenuClose} component="a" href="/zosa-at-work">
         ZOSA For Businesses
         </MenuItem>

        <MenuItem onClick={handleMenuClose} component="a" href="/reviews">
         Reviews
         </MenuItem>

        {/* Guides. Expands in place rather than opening a submenu, so the whole
            cluster stays reachable in one popup on mobile as well as desktop. */}
        <MenuItem
          sx={{ justifyContent: 'space-between', gap: 2 }}
        >
          <ListItemText primary="Guides" primaryTypographyProps={{ variant: 'inherit' }} />
          <Box
            component="a"
            href="/guides"
            onClick={handleMenuClose}
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              color: 'primary.main',
              textDecoration: 'none',
              flexShrink: 0,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            See all
          </Box>
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setOpenGuides((v) => !v);
          }}
          aria-expanded={openGuides}
          aria-controls="zosa-guides-list"
          sx={{ justifyContent: 'space-between', gap: 2, py: 0.25 }}
        >
          <ListItemText
            primary="Browse by topic"
            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
          />
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              color: 'text.secondary',
              transform: openGuides ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s ease',
            }}
          />
        </MenuItem>

        <Collapse
          in={openGuides}
          timeout={220}
          unmountOnExit={false}
          onEntered={repositionMenu}
          onExited={repositionMenu}
        >
          <Box
            id="zosa-guides-list"
            sx={{
              // No scroll container here on purpose. The menu paper is already
              // capped and scrollable, and nesting a second scroll area inside
              // it chains awkwardly on touch.
              pb: 1,
              backgroundColor: (t) =>
                t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            }}
          >
            {GUIDE_GROUPS.map((group) => (
              <Box key={group.heading}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    px: 2,
                    pt: 1.5,
                    pb: 0.5,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {group.heading}
                </Typography>
                {group.links.map((l) => (
                  <MenuItem
                    key={l.to}
                    onClick={handleMenuClose}
                    component="a"
                    href={l.to}
                    sx={{
                      pl: 3,
                      pr: 2,
                      py: 0.75,
                      fontSize: '0.8125rem',
                      color: 'text.secondary',
                      whiteSpace: 'normal',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    {l.label}
                  </MenuItem>
                ))}
              </Box>
            ))}
          </Box>
        </Collapse>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem onClick={handleOpenGettingStarted}>Getting Started</MenuItem>
        
        <MenuItem onClick={handleMenuClose} component="a" href="/important-info">
          Important Info
        </MenuItem>
        <MenuItem onClick={handleMenuClose} component="a" href="/our-purpose">
          Our Purpose
        </MenuItem>
        <MenuItem onClick={handleOpenDataPolicy}>Data Policy</MenuItem>
        <MenuItem onClick={handleOpenFeedback}>Feedback</MenuItem>
        <MenuItem onClick={handleMenuClose} component="a" href="/contact">
          Contact
        </MenuItem>
        <MenuItem onClick={handleMenuClose} component="a" href="/safety">
          Safety
        </MenuItem>
        {!inline && (
          <MenuItem onClick={handleMenuClose} component="a" href="/crisis">
            Crisis
          </MenuItem>
        )}
      </Menu>

      {/* Modals for Getting Started, Data Policy, and Feedback */}
      <HelpModule open={openGettingStarted} handleClose={handleCloseGettingStarted} />
      <DataPolicyDialog open={openDataPolicy} handleClose={handleCloseDataPolicy} />
      <FeedbackDialog open={openFeedback} handleClose={handleCloseFeedback} />
    </>
  );
};

HelpMenu.propTypes = {
  inline: PropTypes.bool,
};

export default HelpMenu;